import { useCallback } from 'react'
import {
  BaseTByCursor,
  InfiniteLoadByCursorProps,
  InfiniteLoadByCursorReturn,
  useInfiniteLoadByCursor,
} from './infiniteLoadByCursor'

type BaseComment = BaseTByCursor & {
  liked?: string
  likes_count: number
}

type InfiniteLoadCommentReturn<T> = InfiniteLoadByCursorReturn<T> & {
  likeItem: (id: string, like_id: string, likes: number) => void
  unlikeItem: (id: string) => void
}

export function useInfiniteLoadComment<T extends BaseComment>(
  props: InfiniteLoadByCursorProps<T>
): InfiniteLoadCommentReturn<T> {
  const { id, getItems, loadingType } = props
  const {
    isLoading,
    items,
    count,
    hasNext,
    loadNext,
    loadMoreRef: observe,
    refresh,
    newItem,
    deleteItem,
    setItems,
  } = useInfiniteLoadByCursor<T>({ id, getItems, loadingType })

  const likeItem = useCallback(
    (id: string, like_id: string, likes: number): void => {
      const newItems = items.map((item) => {
        if (item.id === id) {
          return { ...item, liked: like_id, likes_count: likes }
        }
        return item
      })
      setItems(newItems)
    },
    [items, setItems]
  )

  const unlikeItem = useCallback(
    (id: string): void => {
      const newItems = items.map((item) => {
        if (item.id === id) {
          return { ...item, liked: undefined, likes_count: item.likes_count - 1 }
        }
        return item
      })
      setItems(newItems)
    },
    [items, setItems]
  )

  return {
    isLoading,
    items,
    count,
    hasNext,
    loadNext,
    loadMoreRef: observe,
    refresh,
    newItem,
    likeItem,
    unlikeItem,
    deleteItem,
    setItems,
  }
}
